import React, { forwardRef, useImperativeHandle, useState, useEffect, useMemo } from 'react';
import { Box, Flex, NumberInput, NumberInputField, useToast, Input, Text } from '@chakra-ui/core';
import { StringArrayInput } from '../../../../app/components/StringArrayInput';
import isEqual from 'lodash/isEqual';
import { Column } from 'react-table';
import { NUM_INPUT_FIELD_STYLES, TEXT_INPUT_FIELD_STYLES } from '../constants';
import { ParameterItem, PaneHandle } from '../accountParameter.types';
import { DataTable } from './DataTable';
import { useTranslation } from 'react-i18next';
import { CustomSwitch } from './CustomSwitch';
import Joi from 'joi';

export interface GenericSettingsPaneHandle extends PaneHandle {
  resetDirtyState: () => void;
  validate: () => boolean;
}

interface GenericSettingsPaneProps {
  items: ParameterItem[];
  onSave: (updates: Record<string, any>) => Promise<boolean>;
  setDirty: (isDirty: boolean) => void;
  onRowsChange?: (rows: ParameterItem[]) => void;
  showInlineActions?: boolean;
  externalResetCounter?: number;
  returnAllRowsValues?: boolean;
}

const StringCell = ({
  item,
  setRows,
  error,
}: {
  item: ParameterItem;
  setRows: React.Dispatch<React.SetStateAction<ParameterItem[]>>;
  error: string | undefined;
}) => {
  return (
    <Box>
      <Input
        value={(item.value as string) || ''}
        onChange={(e) => {
          setRows((prev) => prev.map((p) => (p.key === item.key ? { ...p, value: e.target.value } : p)));
        }}
        isInvalid={!!error}
        {...TEXT_INPUT_FIELD_STYLES}
        maxW="360px"
      />
      {error && (
        <Text color="red.500" fontSize="xs" mt={1}>
          {error}
        </Text>
      )}
    </Box>
  );
};

const NumberCell = ({
  item,
  setRows,
  isDirty,
  error,
}: {
  item: ParameterItem;
  setRows: React.Dispatch<React.SetStateAction<ParameterItem[]>>;
  isDirty: boolean;
  error: string | undefined;
}) => {
  return (
    <Box>
      <Flex align="center">
        <NumberInput
          size="sm"
          maxW="88px"
          value={(item.value as number) ?? ''}
          onChange={(_valueAsString, valueAsNumber) => {
            setRows((prev) =>
              prev.map((p) => (p.key === item.key ? { ...p, value: isNaN(valueAsNumber) ? undefined : valueAsNumber } : p)),
            );
          }}
          isInvalid={!!error}
        >
          <NumberInputField {...NUM_INPUT_FIELD_STYLES} />
        </NumberInput>
        {item.unit && (
          <Text ml="10px" fontSize="sm">
            {item.unit}
          </Text>
        )}
      </Flex>
      {error && (
        <Text color="red.500" fontSize="xs" mt={1}>
          {error}
        </Text>
      )}
    </Box>
  );
};

export const GenericSettingsPane = forwardRef<GenericSettingsPaneHandle, GenericSettingsPaneProps>(
  (
    { items, onSave, setDirty, onRowsChange, showInlineActions = true, returnAllRowsValues = false, externalResetCounter },
    ref,
  ) => {
    const toast = useToast();
    const { t } = useTranslation('accountParameter');
    const [initialItems, setInitialItems] = useState<ParameterItem[]>(() => JSON.parse(JSON.stringify(items)));
    const [rows, setRows] = useState<ParameterItem[]>(() => JSON.parse(JSON.stringify(items)));
    const [isDirty, setIsDirty] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const itemsString = useMemo(() => JSON.stringify(items), [items]);

    useEffect(() => {
      if (!isDirty) {
        const newItems = JSON.parse(itemsString);
        setInitialItems(newItems);
        setRows(newItems);
        setErrors({});
      }
    }, [itemsString, isDirty]);

    useEffect(() => {
      const dirty = !isEqual(initialItems, rows);
      setIsDirty(dirty);
      setDirty(dirty);
    }, [rows, initialItems, setDirty]);

    useEffect(() => {
      onRowsChange?.(rows);
    }, [rows, onRowsChange]);

    useEffect(() => {
      if (typeof externalResetCounter === 'number' && externalResetCounter > 0) {
        setRows(initialItems);
      }
    }, [externalResetCounter, initialItems]);

    const validate = () => {
      const newErrors: Record<string, string> = {};
      rows.forEach((row) => {
        const initialItem = initialItems.find((item) => item.key === row.key);
        const isDirty = !isEqual(row.value, initialItem?.value);

        if (isDirty) {
          let isEmpty = false;
          if (row.value === null || row.value === undefined || row.value === '') {
            isEmpty = true;
          }
          if (Array.isArray(row.value) && row.value.length === 0) {
            isEmpty = true;
          }

          if (row.isRequired && isEmpty) {
            newErrors[row.key] = t('genericSettingsPane.fieldIsRequired');
            return; // Skip further validation for this field
          }

          // Format/Constraint validation for non-empty values
          if (!isEmpty) {
            if (row.type === 'string' && row.validationType === 'url') {
              const value = row.value as string;
              const { error } = Joi.string()
                .uri({ scheme: ['http', 'https'] })
                .validate(value);
              if (error) {
                newErrors[row.key] = t('genericSettingsPane.invalidUrl');
              }
            } else if (row.type === 'string' && row.validationType === 'email') {
              const value = row.value as string;
              const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
              if (!emailRegex.test(value)) {
                newErrors[row.key] = t('genericSettingsPane.invalidEmailAddress');
              }
            } else if (row.type === 'number') {
              const value = row.value as number;
              if (row.min !== undefined && value < row.min) {
                newErrors[row.key] = t('genericSettingsPane.valueBelowRecommendedMinimum', { min: row.min });
              } else if (row.max !== undefined && value > row.max) {
                newErrors[row.key] = t('genericSettingsPane.valueAboveRecommendedMaximum', { max: row.max });
              }
            }
          }
        }
      });
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
      if (!validate()) {
        toast({ status: 'error', title: t('genericSettingsPane.pleaseFixValidationErrorsBeforeSaving'), isClosable: true });
        return;
      }

      const updates: Record<string, any> = {};
      rows.forEach((row) => {
        const initialItem = initialItems.find((item) => item.key === row.key);
        if (initialItem && !isEqual(row.value, initialItem.value)) {
          updates[row.key] = row.value;
        }
      });

      if (Object.keys(updates).length > 0) {
        const payload = returnAllRowsValues
          ? rows.reduce((acc, row) => {
              if (row?.isSection) {
                return acc;
              }
              acc[row.key] = row.value;
              return acc;
            }, {} as Record<string, any>)
          : updates;

        const success = await onSave(payload);
        if (success) {
          setInitialItems(JSON.parse(JSON.stringify(rows)));
        }
      } else {
        toast({ status: 'info', title: t('genericSettingsPane.noChangesToSave'), isClosable: true });
      }
    };

    const handleReset = () => {
      setInitialItems(JSON.parse(JSON.stringify(items)));
      setRows(JSON.parse(JSON.stringify(items)));
      setIsDirty(false);
      setDirty(false);
      setErrors({});
    };

    useImperativeHandle(ref, () => ({
      save: handleSave,
      reset: handleReset,
      resetDirtyState: () => {
        setInitialItems(JSON.parse(JSON.stringify(items)));
      },
      validate,
    }));

    const columns = useMemo<Column<ParameterItem>[]>(
      () => [
        {
          Header: t('genericSettingsPane.parameter') as string,
          accessor: 'label',
          Cell: ({ value }: { value: string }) => <Text fontWeight={600}>{value}</Text>,
        },
        {
          Header: t('genericSettingsPane.description') as string,
          accessor: 'description',
          disableSortBy: true,
          Cell: ({ value }: { value: string }) => (
            <Text fontSize="xs" style={{ whiteSpace: 'pre-line' }}>
              {value}
            </Text>
          ),
        },
        {
          Header: t('genericSettingsPane.value') as string,
          id: 'value',
          disableSortBy: true,
          Cell: ({ row }: { row: { original: ParameterItem } }) => {
            const item = row.original;
            const isRowDirty = !isEqual(item.value, initialItems.find((i) => i.key === item.key)?.value);

            if (item.type === 'boolean') {
              return (
                <CustomSwitch
                  isChecked={item.value as boolean}
                  onChange={(nextVal) => {
                    setRows((prev) => prev.map((p) => (p.key === item.key ? { ...p, value: nextVal } : p)));
                  }}
                />
              );
            } else if (item.type === 'string') {
              return <StringCell item={item} setRows={setRows} error={errors[item.key]} />;
            } else if (item.type === 'string[]') {
              return (
                <StringArrayInput
                  value={(item.value as string[]) || []}
                  itemKey={item.key}
                  onValueChange={(key, newValue) => {
                    setRows((prev) => prev.map((p) => (p.key === key ? { ...p, value: newValue } : p)));
                  }}
                  error={errors[item.key]}
                />
              );
            } else if (item.type === 'number') {
              return <NumberCell item={item} setRows={setRows} isDirty={isRowDirty} error={errors[item.key]} />;
            }
            return null;
          },
        },
      ],
      [setRows, initialItems, errors, t],
    );

    return (
      <Box>
        <DataTable
          columns={columns}
          data={rows}
          getRowProps={(row: { original: ParameterItem }) => ({
            className: !isEqual(row.original.value, initialItems.find((item) => item.key === row.original.key)?.value)
              ? 'dirty'
              : '',
          })}
          isSpanningRow={(item) => !!item.isSection}
          renderSpanningRow={(item) => (
            <Text fontWeight={700} fontSize="sm">
              {item.label}
            </Text>
          )}
        />
      </Box>
    );
  },
);
